/* PerfectScore — assets/perfectscore-kit.js
   Runs on the Bearly Tools shell. GA4 tracking goes through window.BT
   (assets/bearly-tools-core.js, loaded first); event names are unchanged:
   ps_tool_view, ps_file_upload, ps_lines_selected, ps_download_click (only when the
   file is actually delivered).
   Download: the export button opens the shared popup (BT.runWithProgress in the core):
   progress -> email step (only if not unlocked) -> file. The core owns the popup, the
   email form, the Klaviyo subscription, the unlock keys and ps_email_view / ps_email_submit /
   ps_email_dismiss / ps_progress_cancel / ps_export_error. This file builds the SVG and
   saves it inside the popup's work step.
   The shell adds ps_related_click and ps_back_click automatically.

   Oct 7 2026 — layered designs:
   - "Layers": a line sits on every filled piece whose area contains it (a tab's score
     line also lies over the bulb body underneath) plus its identical stacked copies.
     Lines on 2+ pieces open a chooser (top / underneath / both); the export carries a
     copy of the dashes inside each chosen piece, and is grouped one <g> per piece.
   - Stacked lines (two or more identical lines on top of each other, e.g. a tab <rect>
     edge drawn over the same edge of the piece outline) are detected on load and selected
     on load. The first click on one opens a small chooser ("There's another line under this
     one — which should get the score?": top / underneath / both) with a "do this for every
     stacked line" checkbox; a ×2 badge marks each stack and reopens the chooser. Dashes are
     exported once per stack.
   - Crossing check is piece-aware: a dash is only dropped for lines in the same piece
     (top-level <g>) or lines physically connected to the scored line. Outlines of a
     neighbouring, overlapping piece no longer cull dashes.
   - A dash is dropped only when another line actually crosses it or ends on it; lines
     that merely come close (converging edges at a pointed tip) no longer cull it.
   - A scored line is split at its junctions (crossings, T-junctions) and each span is
     dashed gap-first, so a junction always falls in a gap and a short line keeps a dash
     on each side of it instead of losing its only dash. */
(function(){
"use strict";

/* ---------------- state ---------------- */
let segments = [];        // {id, pts:[[x,y]...], len, sel, shape, idx, stack}
let shapes = {};          // shapeId -> {closed, count, el, piece}
let originals = {};       // elIdx -> verbatim export markup (transform baked in)
let stacks = {};          // layer sets: stackId -> {ids:[segId...] identical copies, layers:[{el, seg}] bottom→top, mode:"all"|layerIndex, extraOn:{el:true}}
let fillPolys = {};       // elIdx -> [closed polyline...] of filled shapes (which piece a line sits on)

function makeVerbatim(el, cs, m){
  const clone = el.cloneNode(true);
  clone.removeAttribute("transform");
  clone.removeAttribute("id");
  clone.removeAttribute("class");
  clone.removeAttribute("style");
  clone.setAttribute("fill", cs.fill || "none");
  clone.setAttribute("stroke", cs.stroke || "none");
  if(cs.fillRule) clone.setAttribute("fill-rule", cs.fillRule);
  const sw = parseFloat(cs.strokeWidth);
  if(isFinite(sw)) clone.setAttribute("stroke-width", sw);
  const s = new XMLSerializer().serializeToString(clone);
  const eps = 1e-9;
  const ident = Math.abs(m.a-1)<eps && Math.abs(m.d-1)<eps &&
                Math.abs(m.b)<eps && Math.abs(m.c)<eps &&
                Math.abs(m.e)<eps && Math.abs(m.f)<eps;
  if(ident) return s;
  const mm = [m.a,m.b,m.c,m.d,m.e,m.f].map(v=>Math.round(v*1e6)/1e6).join(",");
  return '<g transform="matrix('+mm+')">'+s+'</g>';
}
let vb = null;            // current viewBox {x,y,w,h}
let fitVb = null;
let origRoot = null;      // {vbAttr, wAttr, hAttr}
let artMarkup = "";       // sanitized original artwork, rendered as canvas backdrop
let fileName = "design";
let diag = 100;           // artwork diagonal, drives dash scale
let unitScale = {dash:1, gap:1}; // preset multipliers
let lastPreset = "custom";       // which dash preset is active
let designMeta = {};             // per-file facts, sent with events
let stackChoice = null;         // remembered answer for stacked lines: "all" | "top" | "under"

const $ = id => document.getElementById(id);
const stage = $("stage");
const CORNER_DEG = 28;
const NS = "http://www.w3.org/2000/svg";

/* ps_tool_view is fired by the shared core once the page loads; this adds our detail to it.
   Without the core (shouldn't happen) we fall back to firing it ourselves. */
const signedInAtLoad = !!(window.PS_CUSTOMER && window.PS_CUSTOMER.email);
if(window.BT){ window.BT.viewParams = { ps_signed_in: signedInAtLoad }; }
else { dl("ps_tool_view", { ps_signed_in: signedInAtLoad }); }

/* stack badge styles (kept here so the section file needs no change) */
(function injectCss(){
  if(document.getElementById("ps-stack-css")) return;
  const st = document.createElement("style");
  st.id = "ps-stack-css";
  st.textContent =
    "#ps-app .ps-stack{display:none;cursor:pointer}" +
    "#ps-app .ps-stack.show{display:inline}" +
    "#ps-app .ps-stack circle{fill:#fff;stroke:var(--bt-accent,#D9456B);stroke-width:1.5}" +
    "#ps-app .ps-stack text{font:700 12px/1 system-ui,-apple-system,Segoe UI,sans-serif;fill:var(--bt-accent,#D9456B);text-anchor:middle;dominant-baseline:central;pointer-events:none}" +
    "#ps-app .ps-stack.on circle{fill:var(--bt-accent,#D9456B)}" +
    "#ps-app .ps-stack.on text{fill:#fff}" +
    "#ps-app .seg.stacked.hovered{stroke-width:4}" +
    "#ps-app .ps-chooser{position:absolute;z-index:5;width:240px;padding:14px 14px 12px;border-radius:12px;background:#fff;color:var(--bt-canvas-ink,#222);box-shadow:0 10px 30px rgba(0,0,0,.18);font-size:13px;line-height:1.35}" +
    "#ps-app .ps-chooser__x{position:absolute;top:6px;right:8px;border:0;background:none;font-size:18px;line-height:1;cursor:pointer;color:#888;padding:2px 6px}" +
    "#ps-app .ps-chooser__title{margin:0 18px 2px 0;font-weight:700}" +
    "#ps-app .ps-chooser__sub{margin:0 0 10px;color:#666}" +
    "#ps-app .ps-chooser__opts{display:grid;gap:6px}" +
    "#ps-app .ps-chooser__tag{font-size:11px;font-weight:600;opacity:.75;margin-left:4px}" +
    "#ps-app .ps-chooser__remember{display:flex;gap:7px;align-items:flex-start;margin-top:10px;font-size:12px;color:#555;cursor:pointer}" +
    "#ps-app .ps-chooser__remember input{margin:2px 0 0}" +
    "@media (max-width:640px){#ps-app .ps-chooser{width:min(260px,calc(100% - 16px))}}";
  document.head.appendChild(st);
})();

/* ---------------- upload wiring ---------------- */
$("pickBtn").addEventListener("click", ()=> $("fileInput").click());
$("newFileBtn").addEventListener("click", ()=> $("fileInput").click());
$("fileInput").addEventListener("change", e=>{
  const f = e.target.files[0];
  if(f) readFile(f);
  e.target.value = "";
});
["dragover","dragenter"].forEach(ev=> stage.addEventListener(ev, e=>{
  e.preventDefault(); $("dropzone").classList.add("dragover");
}));
["dragleave","drop"].forEach(ev=> stage.addEventListener(ev, e=>{
  e.preventDefault(); $("dropzone").classList.remove("dragover");
}));
stage.addEventListener("drop", e=>{
  const f = e.dataTransfer.files && e.dataTransfer.files[0];
  if(f) readFile(f);
});

function readFile(f){
  fileName = f.name.replace(/\.svg$/i,"");
  const r = new FileReader();
  r.onload = ()=> loadSvgText(r.result, f.name);
  r.onerror = ()=> showErr("Couldn't read that file. Try again.");
  r.readAsText(f);
}

function showErr(msg){
  const el = $("err");
  el.textContent = msg;
  el.style.display = "block";
}

/* ---------------- sample ---------------- */
$("sampleBtn").addEventListener("click", ()=>{
  fileName = "sample-box";
  loadSvgText(
`<svg xmlns="http://www.w3.org/2000/svg" viewBox="80 80 240 240">
  <path fill="none" stroke="#000" d="M150,150 L150,100 L250,100 L250,150 L300,150 L300,250 L250,250 L250,300 L150,300 L150,250 L100,250 L100,150 Z"/>
  <line x1="150" y1="150" x2="250" y2="150" stroke="#000"/>
  <line x1="250" y1="150" x2="250" y2="250" stroke="#000"/>
  <line x1="250" y1="250" x2="150" y2="250" stroke="#000"/>
  <line x1="150" y1="250" x2="150" y2="150" stroke="#000"/>
${'</'}svg>`, "sample-box.svg");
});

/* ---------------- SVG parsing pipeline ---------------- */
function loadSvgText(text, displayName){
  designMeta = { source_app: detectSourceApp(text), used_sample: displayName === "sample-box.svg" };
  let doc;
  try{
    doc = new DOMParser().parseFromString(
      window.BT && BT.cleanSvgText ? BT.cleanSvgText(text) : String(text).replace(/<!DOCTYPE[^[>]*(\[[\s\S]*?\])?\s*>/i, ""),
      "image/svg+xml");
  }catch(e){ showErr("That doesn't look like a valid SVG."); return; }
  const root = doc.querySelector("svg");
  if(!root || doc.querySelector("parsererror")){ showErr("That doesn't look like a valid SVG."); return; }
  sanitize(root);

  // Normalize root sizing so user units come through 1:1
  origRoot = {
    vbAttr: root.getAttribute("viewBox"),
    wAttr: root.getAttribute("width"),
    hAttr: root.getAttribute("height")
  };
  let vbNums = null;
  if(origRoot.vbAttr){
    vbNums = origRoot.vbAttr.trim().split(/[\s,]+/).map(Number);
  }else{
    const w = parseFloat(origRoot.wAttr)||300, h = parseFloat(origRoot.hAttr)||300;
    vbNums = [0,0,w,h];
    root.setAttribute("viewBox", vbNums.join(" "));
  }
  root.setAttribute("width", vbNums[2]);
  root.setAttribute("height", vbNums[3]);

  // Mount hidden for geometry APIs
  const host = $("hiddenHost");
  host.innerHTML = "";
  const live = document.importNode(root, true);
  host.appendChild(live);
  artMarkup = live.innerHTML;

  const rootM = live.getScreenCTM();
  if(!rootM){ showErr("Couldn't measure this SVG. Try re-exporting it."); return; }
  const rootInv = rootM.inverse();

  const supported = "path,rect,circle,ellipse,line,polyline,polygon";
  const els = live.querySelectorAll(supported);
  let skipped = live.querySelectorAll("text,image,use").length;

  // Pieces: each top-level child of the root is one piece (a layered design exports one
  // <g> per piece). A flat file with no groups is treated as a single piece — exactly
  // the old behaviour, every line checks against every other.
  const topKids = Array.prototype.slice.call(live.children);
  const grouped = topKids.some(k=> k.localName === "g" && k.querySelector(supported));
  const pieceOf = el=>{
    if(!grouped) return 0;
    let t = el;
    while(t.parentNode && t.parentNode !== live) t = t.parentNode;
    const i = topKids.indexOf(t);
    return i < 0 ? 0 : i;
  };

  closeChooser();
  stackChoice = null;
  segments = [];
  shapes = {};
  originals = {};
  stacks = {};
  fillPolys = {};
  let idc = 0, sid = 0, elc = 0;
  els.forEach(el=>{
    // skip invisible
    const cs = getComputedStyle(el);
    if(cs.display==="none" || cs.visibility==="hidden") return;
    const m = rootInv.multiply(el.getScreenCTM());
    const polys = elementToPolylines(el, live);
    if(!polys.length) return;
    const elIdx = elc++;
    originals[elIdx] = makeVerbatim(el, cs, m);
    const piece = pieceOf(el);
    const filled = cs.fill && cs.fill !== "none" && parseFloat(cs.fillOpacity==null?1:cs.fillOpacity) > 0;
    polys.forEach(p=>{
      const pts = p.pts.map(pt=>{
        return [m.a*pt[0]+m.c*pt[1]+m.e, m.b*pt[0]+m.d*pt[1]+m.f];
      });
      if(filled && (p.closed || dist2(pts[0], pts[pts.length-1]) < 1e-6) && pts.length>2) (fillPolys[elIdx] = fillPolys[elIdx]||[]).push(pts);
      const subs = splitAtCorners(pts, p.closed)
        .map(sub=>({pts:sub, len:polyLen(sub)}))
        .filter(sub=>sub.len>1e-6);
      if(!subs.length) return;
      const shapeId = sid++;
      shapes[shapeId] = {closed: !!p.closed, count: subs.length, el: elIdx, piece: piece};
      subs.forEach((sub,k)=>{
        segments.push({id:idc++, pts:sub.pts, len:sub.len, sel:false, shape:shapeId, idx:k, stack:null});
      });
    });
  });

  host.innerHTML = "";

  if(!segments.length){ showErr("No cuttable lines found in that file."); return; }

  // bbox
  let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity;
  segments.forEach(s=> s.pts.forEach(p=>{
    if(p[0]<x0)x0=p[0]; if(p[0]>x1)x1=p[0];
    if(p[1]<y0)y0=p[1]; if(p[1]>y1)y1=p[1];
  }));
  const pad = Math.max(x1-x0, y1-y0)*0.06 || 10;
  fitVb = {x:x0-pad, y:y0-pad, w:(x1-x0)+2*pad, h:(y1-y0)+2*pad};
  vb = Object.assign({}, fitVb);
  diag = Math.hypot(x1-x0, y1-y0) || 100;

  findStacks();
  findLayers();
  const nStacks = Object.keys(stacks).filter(k=> layerCount(stacks[k]) >= 2).length;

  $("filemeta").innerHTML = "<strong>"+esc(displayName)+"</strong><br>"+segments.length+" selectable lines" +
    (nStacks ? "<br>Layered design \u2014 lines on more than one piece show a \u00d72 badge" : "");
  $("warnings").textContent = skipped ? skipped+" element(s) skipped (text, images, or symbols aren't supported yet)." : "";
  $("err").style.display = "none";
  $("dropzone").classList.add("hidden");
  $("zoomer").hidden = false;
  $("hint").hidden = false;

  designMeta.line_count = segments.length;
  designMeta.file_name = displayName;
  designMeta.stack_count = nStacks;
  dl("ps_file_upload", {
    ps_file_name:   displayName,
    ps_line_count:  segments.length,
    ps_stack_count: nStacks,
    ps_source_app:  designMeta.source_app,
    ps_used_sample: designMeta.used_sample
  });
  trackEvent("Started PerfectScore", {
    file_name: displayName,
    line_count: segments.length,
    stack_count: nStacks,
    source_app: designMeta.source_app,
    used_sample: designMeta.used_sample
  });

  buildScene();
  updateSelUI();
  updateSliderLabels();
}

/* Which editor exported this file? Useful product signal, no personal data. */
function detectSourceApp(text){
  const head = text.slice(0, 4000);
  if(/inkscape/i.test(head)) return "Inkscape";
  if(/illustrator|adobe/i.test(head)) return "Illustrator";
  if(/affinity/i.test(head)) return "Affinity";
  if(/figma/i.test(head)) return "Figma";
  if(/canva/i.test(head)) return "Canva";
  if(/corel/i.test(head)) return "CorelDRAW";
  if(/sketch/i.test(head)) return "Sketch";
  return "Unknown";
}

function esc(s){ return s.replace(/[&<>"]/g, c=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[c])); }

function sanitize(root) {
  // Shared cleaner lives in bearly-tools-core.js. Local fallback only if an older core is cached.
  if (window.BT && BT.sanitizeSvg) return BT.sanitizeSvg(root);
  return svgFallbackClean(root);
}
function svgFallbackClean(root, keepDataImages) {
  var drop = { script:1, foreignobject:1, iframe:1, embed:1, object:1, video:1, audio:1, animate:1,
               animatemotion:1, animatetransform:1, animatecolor:1, set:1, discard:1, handler:1, listener:1, feimage:1 };
  if (!keepDataImages) drop.image = 1;
  [root].concat([].slice.call(root.querySelectorAll("*"))).forEach(function (n) {
    var ln = (n.localName || "").toLowerCase();
    if (drop[ln]) { n.remove(); return; }
    var scrub = function (css) { return String(css).replace(/@import[^;]*;?/gi, "").replace(/url\(\s*(?!["']?#)[^)]*\)/gi, "none"); };
    if (ln === "style") n.textContent = scrub(n.textContent);
    [].slice.call(n.attributes).forEach(function (a) {
      var l = (a.localName || "").toLowerCase(), v = String(a.value).replace(/[\u0000- ]+/g, "").toLowerCase();
      var keepImg = keepDataImages && ln === "image" && /^data:image\/(png|jpe?g|gif|webp|avif|bmp);/.test(v);
      if (/^on/i.test(a.name) || ((l === "href" || l === "src") && v.charAt(0) !== "#" && !keepImg) || (!keepImg && /^(javascript|vbscript|data):/.test(v))) n.removeAttributeNode(a);
      else if (/url\(/i.test(a.value)) { var c = scrub(a.value); if (c !== a.value) n.setAttribute(a.name, c); }
    });
  });
  return root;
}

/* Convert one element to raw (untransformed) polylines */
function elementToPolylines(el, liveRoot){
  const tag = el.tagName.toLowerCase();
  const out = [];
  const num = a=> parseFloat(el.getAttribute(a))||0;

  if(tag==="line"){
    out.push({pts:[[num("x1"),num("y1")],[num("x2"),num("y2")]], closed:false});
  }
  else if(tag==="polyline" || tag==="polygon"){
    const raw = (el.getAttribute("points")||"").trim().split(/[\s,]+/).map(Number);
    const pts=[];
    for(let i=0;i+1<raw.length;i+=2) pts.push([raw[i],raw[i+1]]);
    if(pts.length>1) out.push({pts, closed: tag==="polygon"});
  }
  else if(tag==="rect"){
    const x=num("x"),y=num("y"),w=num("width"),h=num("height");
    const rx=parseFloat(el.getAttribute("rx"))||0;
    if(rx>0){ out.push(samplePathD(roundedRectD(x,y,w,h,rx), liveRoot, true)); }
    else out.push({pts:[[x,y],[x+w,y],[x+w,y+h],[x,y+h]], closed:true});
  }
  else if(tag==="circle" || tag==="ellipse"){
    const cx=num("cx"), cy=num("cy");
    const rx = tag==="circle"? num("r") : num("rx");
    const ry = tag==="circle"? num("r") : num("ry");
    const n = Math.max(48, Math.min(256, Math.round(Math.PI*(rx+ry)/1.2)));
    const pts=[];
    for(let i=0;i<n;i++){
      const a = i/n*2*Math.PI;
      pts.push([cx+rx*Math.cos(a), cy+ry*Math.sin(a)]);
    }
    out.push({pts, closed:true});
  }
  else if(tag==="path"){
    const d = el.getAttribute("d")||"";
    const parts = d.split(/(?=[Mm])/).map(s=>s.trim()).filter(Boolean);
    let cur=[0,0];
    parts.forEach(part=>{
      let sub = part;
      if(sub[0]==="m"){
        const mm = sub.match(/^m\s*(-?\d*\.?\d+(?:e[-+]?\d+)?)[\s,]+(-?\d*\.?\d+(?:e[-+]?\d+)?)\s*([\s\S]*)$/);
        if(mm){
          const mx=cur[0]+parseFloat(mm[1]), my=cur[1]+parseFloat(mm[2]);
          let rest=mm[3]||"";
          if(rest && /^[-.\d]/.test(rest)) rest="l "+rest; // implicit linetos after m are relative
          sub="M"+mx+" "+my+(rest?" "+rest:"");
        }
      }
      const closed=/z\s*$/i.test(sub);
      let r=null;
      if(/[csqta]/i.test(sub.slice(1))) r=samplePathD(sub, liveRoot, closed);
      else r=parseLinearSubpath(sub);
      if(r && r.pts && r.pts.length>1){
        out.push(r);
        cur = r.closed? r.pts[0].slice() : r.pts[r.pts.length-1].slice();
      }
    });
  }
  return out.filter(p=>p && p.pts && p.pts.length>1);
}

function roundedRectD(x,y,w,h,r){
  r=Math.min(r,w/2,h/2);
  return "M"+(x+r)+","+y+" H"+(x+w-r)+" A"+r+","+r+" 0 0 1 "+(x+w)+","+(y+r)+
    " V"+(y+h-r)+" A"+r+","+r+" 0 0 1 "+(x+w-r)+","+(y+h)+
    " H"+(x+r)+" A"+r+","+r+" 0 0 1 "+x+","+(y+h-r)+
    " V"+(y+r)+" A"+r+","+r+" 0 0 1 "+(x+r)+","+y+" Z";
}

function parseLinearSubpath(d){
  const toks = d.match(/[MmLlHhVvZz]|-?\d*\.?\d+(?:e[-+]?\d+)?/g);
  if(!toks) return null;
  let i=0, cmd=null, cur=[0,0], start=null, closed=false;
  const pts=[];
  const nextN = ()=> parseFloat(toks[i++]);
  while(i<toks.length){
    const t = toks[i];
    if(/[A-Za-z]/.test(t)){ cmd=t; i++; }
    switch(cmd){
      case "M": cur=[nextN(),nextN()]; cmd="L"; break;
      case "m": cur=[cur[0]+nextN(),cur[1]+nextN()]; cmd="l"; break;
      case "L": cur=[nextN(),nextN()]; break;
      case "l": cur=[cur[0]+nextN(),cur[1]+nextN()]; break;
      case "H": cur=[nextN(),cur[1]]; break;
      case "h": cur=[cur[0]+nextN(),cur[1]]; break;
      case "V": cur=[cur[0],nextN()]; break;
      case "v": cur=[cur[0],cur[1]+nextN()]; break;
      case "Z": case "z": closed=true; i=toks.length; break;
      default: i++; continue;
    }
    if(!start) start=cur.slice();
    pts.push(cur.slice());
  }
  if(pts.length<2) return null;
  return {pts, closed};
}

function samplePathD(d, liveRoot, closed){
  const tmp = document.createElementNS(NS,"path");
  tmp.setAttribute("d", d);
  liveRoot.appendChild(tmp);
  let L;
  try{ L = tmp.getTotalLength(); }catch(e){ tmp.remove(); return null; }
  if(!isFinite(L) || L<=0){ tmp.remove(); return null; }
  const n = Math.max(16, Math.min(1200, Math.ceil(L/Math.max(0.25, L/600))));
  const pts=[];
  for(let i=0;i<=n;i++){
    const p = tmp.getPointAtLength(L*i/n);
    pts.push([p.x,p.y]);
  }
  tmp.remove();
  return {pts, closed:!!closed};
}

/* corner-split a polyline (or loop) into straight/smooth runs */
function splitAtCorners(pts, closed){
  pts = dedupe(pts);
  if(pts.length<3) return [pts];
  const isCorner = i=>{
    const a=pts[(i-1+pts.length)%pts.length], b=pts[i], c=pts[(i+1)%pts.length];
    const v1=[b[0]-a[0],b[1]-a[1]], v2=[c[0]-b[0],c[1]-b[1]];
    const l1=Math.hypot(v1[0],v1[1]), l2=Math.hypot(v2[0],v2[1]);
    if(l1<1e-9||l2<1e-9) return false;
    const dot=(v1[0]*v2[0]+v1[1]*v2[1])/(l1*l2);
    return Math.acos(Math.max(-1,Math.min(1,dot)))*180/Math.PI > CORNER_DEG;
  };
  const corners=[];
  for(let i= closed?0:1; i< (closed? pts.length : pts.length-1); i++){
    if(isCorner(i)) corners.push(i);
  }
  if(closed){
    if(!corners.length) { pts.push(pts[0].slice()); return [pts]; } // circle-like loop
    const seq = pts.slice(corners[0]).concat(pts.slice(0,corners[0]+1)); // rotate to start at corner, wrap
    return splitAtCorners(seq, false);
  }
  if(!corners.length) return [pts];
  const out=[]; let prev=0;
  corners.forEach(ci=>{ out.push(pts.slice(prev, ci+1)); prev=ci; });
  out.push(pts.slice(prev));
  return out.filter(s=>s.length>1);
}

function dedupe(pts){
  const out=[pts[0]];
  for(let i=1;i<pts.length;i++){
    const p=pts[i], q=out[out.length-1];
    if(Math.hypot(p[0]-q[0],p[1]-q[1])>1e-7) out.push(p);
  }
  return out;
}

function polyLen(pts){
  let L=0;
  for(let i=1;i<pts.length;i++) L+=Math.hypot(pts[i][0]-pts[i-1][0],pts[i][1]-pts[i-1][1]);
  return L;
}

/* ---------------- stacked lines ----------------
   Two lines are a stack when they trace the same path (every sample of one lies on the
   other, lengths within 5%). Layered designs produce these all the time: a tab <rect>
   edge drawn over the same edge of the outline, or two pieces sharing a glued edge.
   Scoring only the top copy is pointless (the copy underneath still cuts straight
   through), so a stack is selected as one. */
function findStacks(){
  stacks = {};
  segments.forEach(s=>{ s.stack = null; s._bb = bboxOf(s.pts); });
  if(segments.length > 4000) return;             // huge files: skip, keep the UI snappy
  const tol = Math.max(diag/1000, 0.05);
  const minLen = Math.max(diag/200, 1);            // specks inside letter curves aren't stacks
  const parent = {};
  const find = a=>{ while(parent[a]!==undefined && parent[a]!==a) a = parent[a]; return a; };
  const union = (a,b)=>{ a=find(a); b=find(b); if(a!==b) parent[a]=b; };
  const sampleOnto = (a,b)=>{
    const n = Math.min(9, a.pts.length);
    for(let k=0;k<n;k++){
      const p = a.pts[Math.round(k*(a.pts.length-1)/Math.max(1,n-1))];
      if(minDistToPoly(p, b.pts) > tol) return false;
    }
    return true;
  };
  for(let i=0;i<segments.length;i++){
    const a = segments[i];
    for(let j=i+1;j<segments.length;j++){
      const b = segments[j];
      if(a.len < minLen || b.len < minLen) continue;
      if(Math.abs(a.len-b.len) > 0.05*Math.max(a.len,b.len)) continue;
      const ab=a._bb, bb=b._bb;
      if(Math.abs(ab[0]-bb[0])>tol || Math.abs(ab[1]-bb[1])>tol || Math.abs(ab[2]-bb[2])>tol || Math.abs(ab[3]-bb[3])>tol) continue;
      if(sampleOnto(a,b) && sampleOnto(b,a)){ parent[a.id] = parent[a.id]===undefined? a.id : parent[a.id]; parent[b.id] = parent[b.id]===undefined? b.id : parent[b.id]; union(a.id,b.id); }
    }
  }
  const groups = {};
  Object.keys(parent).forEach(k=>{ const r = find(+k); (groups[r] = groups[r]||[]).push(+k); });
  let sidx = 0;
  Object.keys(groups).forEach(r=>{
    const ids = groups[r].sort((x,y)=>x-y);      // document order: last one is drawn on top
    if(ids.length<2) return;
    const st = { id: sidx++, ids: ids, mode: "all" };
    stacks[st.id] = st;
    ids.forEach(id=>{ segments[id].stack = st.id; });
  });
}
/* ---------------- layers ----------------
   Which pieces does a line sit on? Its own identical copies (a stack) plus every filled
   shape whose area contains the line — e.g. a tab's score line drawn over the bulb body.
   A line on two or more pieces gets a layer set: one click scores the chosen pieces and
   the export carries a copy of the dashes inside each of those pieces. */
function pointInPolys(pt, polys){
  let inside = false;
  polys.forEach(poly=>{
    for(let i=0, j=poly.length-1; i<poly.length; j=i++){
      const a=poly[i], b=poly[j];
      if(((a[1]>pt[1]) !== (b[1]>pt[1])) && (pt[0] < (b[0]-a[0])*(pt[1]-a[1])/((b[1]-a[1])||1e-12)+a[0])) inside = !inside;
    }
  });
  return inside;
}
function containersOf(s, excludeEls){
  // the whole line must lie on the piece: interior samples inside, both ends inside or on its edge
  const cum = cumLens(s.pts), total = cum[cum.length-1];
  const tests = [0.25,0.5,0.75].map(f=> polyAt(s.pts, cum, total*f));
  const ends = [s.pts[0], s.pts[s.pts.length-1]];
  const tol = Math.max(diag/500, 0.5);
  const out = [];
  Object.keys(fillPolys).forEach(k=>{
    const e = +k;
    if(excludeEls.indexOf(e)>=0) return;
    const polys = fillPolys[e];
    if(!polys._bb) polys._bb = bboxOf([].concat.apply([], polys));
    const bb = polys._bb;
    const inBox = pt=> pt[0]>=bb[0]-tol && pt[0]<=bb[2]+tol && pt[1]>=bb[1]-tol && pt[1]<=bb[3]+tol;
    if(!tests.every(pt=> inBox(pt) && pointInPolys(pt, polys))) return;
    if(!ends.every(pt=> inBox(pt) && (pointInPolys(pt, polys) || polys.some(poly=> minDistToPoly(pt, poly.concat([poly[0]])) < tol)))) return;
    out.push(e);
  });
  return out;
}
function findLayers(){
  if(segments.length > 4000) return;
  const minLen = Math.max(diag/200, 1);
  let sidx = Object.keys(stacks).length;
  segments.forEach(s=>{
    if(s.len < minLen) return;
    if(s.stack!==null && stacks[s.stack].layers) return;          // already built via a copy
    const ids = s.stack!==null ? stacks[s.stack].ids : [s.id];
    const copyEls = ids.map(id=> shapes[segById(id).shape].el);
    const ownFilled = copyEls.filter(e=> fillPolys[e]);
    const extra = containersOf(s, copyEls);
    const layers = ownFilled.map(e=> ({el:e, seg: ids[copyEls.indexOf(e)]}))
      .concat(extra.map(e=> ({el:e, seg:null})))
      .sort((a,b)=> a.el-b.el);                                     // document order: last = top
    let st = s.stack!==null ? stacks[s.stack] : null;
    // a plain line on nothing (or a filled shape's own edge on nothing) needs no set
    if(!st && !extra.length) return;
    if(!st){ st = { id: sidx++, ids: ids, mode: "all" }; stacks[st.id] = st; ids.forEach(id=>{ segById(id).stack = st.id; }); }
    st.layers = layers;
    st.extraOn = {};
  });
  // stacks whose copies are all unfilled lines and sit on nothing: not a layer set
  Object.keys(stacks).forEach(k=>{ const st = stacks[k]; if(!st.layers) { st.layers = []; st.extraOn = {}; } });
}
function minDistToPoly(p, pts){
  let m = Infinity;
  for(let j=1;j<pts.length;j++){ const d = ptSegDist(p, pts[j-1], pts[j]); if(d<m) m=d; }
  return m;
}
const segById = id=> segments[id];
/* the member of a stack whose dashes are drawn/exported: the first selected one */
function stackLeader(st){
  for(const id of st.ids){ if(segById(id).sel) return id; }
  return st.ids[0];
}
function drawsDashes(s){
  if(!s.sel) return false;
  if(s.stack===null) return true;
  return stackLeader(stacks[s.stack]) === s.id;
}
const layerCount = st=> st.layers ? st.layers.length : st.ids.length;
function layerName(st, i){
  const n = layerCount(st);
  if(i===n-1) return "Top piece";
  if(n===2) return "Piece underneath";
  if(i===0) return "Bottom piece";
  return "Piece "+(n-i)+" of "+n;
}
/* apply a choice to a layer set: "all", a layer index, or "none" */
function applyStackChoice(st, choice){
  const copies = st.ids.map(segById);
  const layers = st.layers && st.layers.length ? st.layers : copies.map(c=> ({el: shapes[c.shape].el, seg: c.id}));
  st.extraOn = {};
  if(choice==="none"){ copies.forEach(c=>{ c.sel=false; }); }
  else{
    st.mode = choice;
    const chosenCopies = {};
    layers.forEach((L,i)=>{
      const on = choice==="all" || i===choice;
      if(!on) return;
      if(L.seg!==null) chosenCopies[L.seg] = true; else st.extraOn[L.el] = true;
    });
    // copies that are themselves a layer (a filled piece's edge) follow the choice; bare
    // score lines (fill none) are never a layer, so every copy is removed from the cut
    // whenever any piece gets the score — otherwise a leftover copy exports as a solid cut line
    const isLayerSeg = {};
    layers.forEach(L=>{ if(L.seg!==null) isLayerSeg[L.seg] = true; });
    copies.forEach(c=>{ c.sel = isLayerSeg[c.id] ? !!chosenCopies[c.id] : true; });
  }
  copies.forEach(paintSeg);
  paintBadge(st);
  updateSelUI();
}
function currentChoice(st){
  const copies = st.ids.map(segById);
  if(!copies.some(c=>c.sel)) return -1;
  const layers = st.layers && st.layers.length ? st.layers : copies.map(c=> ({el: shapes[c.shape].el, seg: c.id}));
  const on = layers.map(L=> L.seg!==null ? segById(L.seg).sel : !!st.extraOn[L.el]);
  if(on.every(Boolean)) return "all";
  const idx = on.indexOf(true);
  return idx;
}
/* click on a layered line: use the remembered choice, or ask */
function clickStack(st, clientX, clientY){
  if(stackChoice!==null){
    const n = layerCount(st);
    const choice = stackChoice==="all" ? "all" : (stackChoice==="top" ? n-1 : 0);
    const already = currentChoice(st) === choice;
    applyStackChoice(st, already ? "none" : choice);
    return;
  }
  openChooser(st, clientX, clientY);
}
/* ---------------- layer chooser popover ----------------
   "This line sits on another piece underneath — which should get the score?" */
let chooser = null;
function closeChooser(){ if(chooser){ chooser.remove(); chooser=null; } }
function openChooser(st, clientX, clientY){
  closeChooser();
  const n = layerCount(st);
  const current = currentChoice(st);
  const box = document.createElement("div");
  box.className = "ps-chooser";
  box.setAttribute("role","dialog");
  box.setAttribute("aria-label","Layered pieces");
  const opts = [];
  for(let i=n-1;i>=0;i--) opts.push({v:i, label: layerName(st,i)});
  opts.push({v:"all", label: n===2 ? "Both pieces" : "All "+n+" pieces"});
  box.innerHTML =
    '<button type="button" class="ps-chooser__x" aria-label="Close">\u00d7</button>' +
    '<p class="ps-chooser__title">'+(n===2 ? "This line sits on another piece underneath." : "This line sits on "+n+" pieces.")+'</p>' +
    '<p class="ps-chooser__sub">Which should get the score?</p>' +
    '<div class="ps-chooser__opts">' +
      opts.map(o=> '<button type="button" class="bt-btn bt-btn--sm bt-btn--block'+(o.v===current?'':' bt-btn--ghost')+'" data-v="'+o.v+'">'+o.label+(o.v==="all"?' <span class="ps-chooser__tag">recommended</span>':'')+'</button>').join("") +
      (current!==-1 ? '<button type="button" class="bt-btn bt-btn--sm bt-btn--block bt-btn--ghost" data-v="none">Unmark</button>' : '') +
    '</div>' +
    '<label class="ps-chooser__remember"><input type="checkbox" checked> Do this for every layered line in this design</label>';
  stage.appendChild(box);
  chooser = box;
  // place near the click, inside the stage
  const r = stage.getBoundingClientRect();
  const bw = box.offsetWidth || 240, bh = box.offsetHeight || 200;
  let x = clientX - r.left + 14, y = clientY - r.top - 20;
  if(x + bw > r.width - 8) x = clientX - r.left - bw - 14;
  if(x < 8) x = 8;
  if(y + bh > r.height - 8) y = r.height - bh - 8;
  if(y < 8) y = 8;
  box.style.left = x+"px"; box.style.top = y+"px";
  box.querySelector(".ps-chooser__x").addEventListener("click", closeChooser);
  box.querySelectorAll("[data-v]").forEach(b=>{
    b.addEventListener("click", ()=>{
      const v = b.dataset.v;
      const choice = v==="all" ? "all" : v==="none" ? "none" : parseInt(v,10);
      const remember = box.querySelector("input").checked;
      applyStackChoice(st, choice);
      const word = choice==="all" ? "all" : choice==="none" ? "none" : (choice===n-1 ? "top" : "under");
      if(remember && choice!=="none") stackChoice = word;
      dl("ps_stack_choice", { ps_choice: word, ps_remember: !!remember });
      closeChooser();
      if(remember && choice!=="none") toast("Layered lines: "+(choice==="all" ? "every piece" : layerName(st,choice).toLowerCase())+". Tap a \u00d7"+n+" badge to change.");
    });
  });
  // stop the stage from treating taps inside the popover as pans/clicks
  ["pointerdown","pointerup","wheel"].forEach(ev=> box.addEventListener(ev, e=> e.stopPropagation()));
}
function toast(msg){
  try{ if(window.BT && typeof window.BT.toast === "function"){ window.BT.toast(msg); return; } }catch(e){}
  const h = $("hint"); if(h){ h.textContent = msg; h.hidden = false; }
}

/* ---------------- scene / interaction ---------------- */
let sceneSvg=null;
let badgeLayer=null;

function buildScene(){
  if(sceneSvg) sceneSvg.remove();
  sceneSvg = document.createElementNS(NS,"svg");
  sceneSvg.setAttribute("xmlns",NS);

  // cutting-mat grid
  const grid = niceStep(diag/24);
  const defs = document.createElementNS(NS,"defs");
  defs.innerHTML =
    '<pattern id="matgrid" width="'+grid+'" height="'+grid+'" patternUnits="userSpaceOnUse">'+
    '<path d="M '+grid+' 0 L 0 0 0 '+grid+'" fill="none" stroke="#D5DACF" stroke-width="0.6" vector-effect="non-scaling-stroke"/></pattern>';
  sceneSvg.appendChild(defs);
  const bgRect = document.createElementNS(NS,"rect");
  bgRect.setAttribute("x",fitVb.x - fitVb.w); bgRect.setAttribute("y",fitVb.y - fitVb.h);
  bgRect.setAttribute("width",fitVb.w*3); bgRect.setAttribute("height",fitVb.h*3);
  bgRect.setAttribute("fill","url(#matgrid)");
  sceneSvg.appendChild(bgRect);

  // original artwork backdrop (full color, non-interactive)
  const art = document.createElementNS(NS,"g");
  art.setAttribute("pointer-events","none");
  art.innerHTML = artMarkup;
  sceneSvg.appendChild(art);

  segments.forEach(s=>{
    const ptStr = s.pts.map(p=>p[0]+","+p[1]).join(" ");
    const halo = document.createElementNS(NS,"polyline");
    halo.setAttribute("points", ptStr);
    halo.setAttribute("class","halo");
    halo.id = "halo"+s.id;
    const vis = document.createElementNS(NS,"polyline");
    vis.setAttribute("points", ptStr);
    vis.setAttribute("class","seg"+(s.stack!==null?" stacked":""));
    vis.id = "seg"+s.id;
    const hit = document.createElementNS(NS,"polyline");
    hit.setAttribute("points", ptStr);
    hit.setAttribute("class","hit");
    hit.__seg = s;
    hit.addEventListener("pointerenter", ()=> hoverSeg(s, true));
    hit.addEventListener("pointerleave", ()=> hoverSeg(s, false));
    sceneSvg.appendChild(halo);
    sceneSvg.appendChild(vis);
    sceneSvg.appendChild(hit);
  });

  // stack badges sit above everything
  badgeLayer = document.createElementNS(NS,"g");
  badgeLayer.setAttribute("class","ps-stacks");
  Object.keys(stacks).forEach(k=>{
    const st = stacks[k];
    if(layerCount(st) < 2) return;
    const lead = segById(st.ids[st.ids.length-1]);
    const mid = midAndNormal(lead.pts, 0.3);
    // badge sits outside the shape the line belongs to (away from that shape's centre),
    // a third of the way along the line so it never covers a score line crossing the middle
    const own = bboxOf([].concat.apply([], segments.filter(x=> x.shape===lead.shape).map(x=> x.pts)));
    const cx = (own[0]+own[2])/2, cy = (own[1]+own[3])/2;
    if((mid[0]-cx)*mid[2] + (mid[1]-cy)*mid[3] < 0){ mid[2] = -mid[2]; mid[3] = -mid[3]; }
    const g = document.createElementNS(NS,"g");
    g.setAttribute("class","ps-stack");
    g.id = "stack"+st.id;
    g.__stack = st;
    g.dataset.mx = mid[0]; g.dataset.my = mid[1]; g.dataset.nx = mid[2]; g.dataset.ny = mid[3];
    const c = document.createElementNS(NS,"circle");
    c.setAttribute("r","13");
    const t = document.createElementNS(NS,"text");
    t.textContent = "\u00d7"+layerCount(st);
    g.appendChild(c); g.appendChild(t);
    g.addEventListener("pointerenter", ()=>{ st.hover = true; st.ids.forEach(id=> hoverSeg(segById(id), true, true)); });
    g.addEventListener("pointerleave", ()=>{ st.hover = false; st.ids.forEach(id=> hoverSeg(segById(id), false, true)); showBadge(st); });
    badgeLayer.appendChild(g);
  });
  sceneSvg.appendChild(badgeLayer);

  stage.insertBefore(sceneSvg, $("dropzone"));
  applyVb();
  segments.forEach(paintSeg);
  Object.keys(stacks).forEach(k=> paintBadge(stacks[k]));
}

function midAndNormal(pts, frac){
  const total = polyLen(pts), target = total*(frac==null ? 0.5 : frac);
  let acc = 0;
  for(let i=1;i<pts.length;i++){
    const d = Math.hypot(pts[i][0]-pts[i-1][0], pts[i][1]-pts[i-1][1]);
    if(acc + d >= target || i===pts.length-1){
      const t = d ? (target-acc)/d : 0;
      const mx = pts[i-1][0]+(pts[i][0]-pts[i-1][0])*t, my = pts[i-1][1]+(pts[i][1]-pts[i-1][1])*t;
      const nx = -(pts[i][1]-pts[i-1][1])/(d||1), ny = (pts[i][0]-pts[i-1][0])/(d||1);
      return [mx,my,nx,ny];
    }
    acc += d;
  }
  return [pts[0][0], pts[0][1], 0, -1];
}

function hoverSeg(s, on, fromBadge){
  const ids = s.stack!==null ? stacks[s.stack].ids : [s.id];
  ids.forEach(id=>{ const el = $("seg"+id); if(el) el.classList.toggle("hovered", on); });
  if(s.stack!==null && !fromBadge){ stacks[s.stack].lineHover = on; showBadge(stacks[s.stack]); }
}
/* badge is visible while a member is selected, hovered, or the badge itself is hovered;
   hiding waits a beat so the pointer can travel from the line onto the badge */
function showBadge(st){
  const b = $("stack"+st.id);
  if(!b) return;
  const keep = ()=> !!(st.hover || st.lineHover || st.ids.some(id=> segById(id).sel));
  clearTimeout(st._hideT);
  if(keep()){ b.classList.add("show"); return; }
  st._hideT = setTimeout(()=>{ if(!keep()) b.classList.remove("show"); }, 180);
}

function niceStep(v){
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const m = v/p;
  return (m<1.5?1: m<3.5?2: m<7.5?5:10)*p;
}

function paintSeg(s){
  const el = $("seg"+s.id);
  const halo = $("halo"+s.id);
  if(!el) return;
  el.classList.toggle("sel", s.sel);
  if(halo) halo.classList.toggle("on", s.sel);
  let grp = $("dash"+s.id);
  if(drawsDashes(s)){
    const {dash,gap} = dashSizes();
    if(!grp){
      grp = document.createElementNS(NS,"g");
      grp.id = "dash"+s.id;
      grp.setAttribute("pointer-events","none");
      sceneSvg.insertBefore(grp, badgeLayer);
    }
    // exact same geometry the export produces — preview is truthful at any zoom
    while(grp.firstChild) grp.removeChild(grp.firstChild);
    dashesForSeg(s, dash, gap).forEach(p=>{
      const dp = document.createElementNS(NS,"polyline");
      dp.setAttribute("points", p.map(q=>q[0]+","+q[1]).join(" "));
      dp.setAttribute("class","dashpiece");
      dp.setAttribute("fill","none");
      dp.setAttribute("stroke","#ee0000");
      dp.setAttribute("stroke-width","2.2");
      dp.setAttribute("vector-effect","non-scaling-stroke");
      grp.appendChild(dp);
    });
  }else if(grp){
    grp.remove();
  }
}

function paintBadge(st){
  const b = $("stack"+st.id);
  if(!b) return;
  const anySel = st.ids.some(id=> segById(id).sel);
  b.classList.toggle("on", anySel);
  showBadge(st);
  const t = b.querySelector("text");
  const n = layerCount(st);
  if(t) t.textContent = st.mode==="all" ? "\u00d7"+n : (st.mode===n-1 ? "top" : (n===2 ? "under" : (n-st.mode)+"/"+n));
}

function placeBadges(){
  if(!badgeLayer || !vb) return;
  const r = stage.getBoundingClientRect();
  // SVG units per CSS pixel. The scene uses the default xMidYMid meet, so the real scale
  // is set by whichever axis is tighter — a tall design in a wide stage is limited by height.
  const k = Math.max(vb.w / (r.width || 1), vb.h / (r.height || 1));
  Array.prototype.forEach.call(badgeLayer.children, g=>{
    const mx=+g.dataset.mx, my=+g.dataset.my, nx=+g.dataset.nx, ny=+g.dataset.ny;
    g.setAttribute("transform", "translate("+(mx+nx*20*k)+","+(my+ny*20*k)+") scale("+k+")");
  });
}

function updateSelUI(){
  const n = segments.filter(s=>s.sel).length;
  $("selCount").textContent = n;
  $("selWord").textContent = n===1? "line":"lines";
  $("exportBtn").disabled = n===0;
}

$("clearBtn").addEventListener("click", ()=>{
  closeChooser();
  segments.forEach(s=>{ s.sel=false; paintSeg(s); });
  Object.keys(stacks).forEach(k=>{ stacks[k].mode = "all"; stacks[k].extraOn = {}; paintBadge(stacks[k]); });
  updateSelUI();
});

/* pan / zoom. A finger wobbles more than a mouse, so a touch only turns into a pan after 10px
   (3px for a mouse). Two fingers pinch-zoom around their midpoint. */
let panStart=null, pinch=null;
const fingers=new Map();
function fingerUp(e){ fingers.delete(e.pointerId); if(fingers.size<2) pinch=null; }
stage.addEventListener("pointerdown", e=>{
  if(!sceneSvg || e.target.closest("#zoomer")) return;
  if(chooser && !e.target.closest(".ps-chooser")) closeChooser();
  e.preventDefault();
  try{ stage.setPointerCapture(e.pointerId); }catch(err){}
  if(e.pointerType==="touch"){
    fingers.set(e.pointerId,{x:e.clientX,y:e.clientY});
    if(fingers.size===2){
      const [a,b]=[...fingers.values()];
      pinch={d:Math.hypot(a.x-b.x,a.y-b.y)||1, vb:Object.assign({},vb), mx:(a.x+b.x)/2, my:(a.y+b.y)/2};
      panStart=null;
      return;
    }
  }
  panStart={x:e.clientX,y:e.clientY,vb:Object.assign({},vb),moved:false,target:e.target,slop:e.pointerType==="mouse"?3:10};
});
stage.addEventListener("pointermove", e=>{
  if(fingers.has(e.pointerId)) fingers.set(e.pointerId,{x:e.clientX,y:e.clientY});
  if(pinch && fingers.size>=2){
    const [a,b]=[...fingers.values()], r=stage.getBoundingClientRect();
    const f=pinch.d/(Math.hypot(a.x-b.x,a.y-b.y)||1);
    const ax=pinch.vb.x+(pinch.mx-r.left)/r.width*pinch.vb.w, ay=pinch.vb.y+(pinch.my-r.top)/r.height*pinch.vb.h;
    vb.w=pinch.vb.w*f; vb.h=pinch.vb.h*f;
    vb.x=ax-((a.x+b.x)/2-r.left)/r.width*vb.w;
    vb.y=ay-((a.y+b.y)/2-r.top)/r.height*vb.h;
    applyVb();
    return;
  }
  if(!panStart) return;
  const dx=e.clientX-panStart.x, dy=e.clientY-panStart.y;
  if(Math.hypot(dx,dy)>panStart.slop) panStart.moved=true;
  if(panStart.moved){
    sceneSvg.classList.add("panning");
    const r=stage.getBoundingClientRect();
    vb.x = panStart.vb.x - dx*(vb.w/r.width);
    vb.y = panStart.vb.y - dy*(vb.h/r.height);
    applyVb();
  }
});
function endPan(e){
  if(e && e.pointerId!=null && stage.hasPointerCapture && stage.hasPointerCapture(e.pointerId)){
    try{ stage.releasePointerCapture(e.pointerId); }catch(err){}
  }
  panStart = null;
  if(sceneSvg) sceneSvg.classList.remove("panning");
}
stage.addEventListener("pointercancel", e=>{ fingerUp(e); endPan(e); });
window.addEventListener("pointerup", e=>{ fingerUp(e); if(panStart) endPan(e); });
stage.addEventListener("pointerup", e=>{
  if(panStart && !panStart.moved && panStart.target){
    const badge = panStart.target.closest ? panStart.target.closest(".ps-stack") : null;
    if(badge && badge.__stack){
      openChooser(badge.__stack, e.clientX, e.clientY);
    }else if(panStart.target.__seg){
      const s = panStart.target.__seg;
      if(s.stack!==null && layerCount(stacks[s.stack]) >= 2 && !e.altKey){
        clickStack(stacks[s.stack], e.clientX, e.clientY);
      }else{
        s.sel = !s.sel;
        paintSeg(s);
        if(s.stack!==null){ stacks[s.stack].ids.forEach(id=> paintSeg(segById(id))); paintBadge(stacks[s.stack]); }
      }
      if(segments.some(x=>x.sel) && !designMeta.selected_once){
        designMeta.selected_once = true;
        dl("ps_lines_selected", { ps_file_name: designMeta.file_name || "unknown" });
      }
      updateSelUI();
    }
  }
  endPan(e);
});
stage.addEventListener("wheel", e=>{
  if(!sceneSvg) return;
  e.preventDefault();
  const r=stage.getBoundingClientRect();
  const mx = vb.x + (e.clientX-r.left)/r.width*vb.w;
  const my = vb.y + (e.clientY-r.top)/r.height*vb.h;
  const f = e.deltaY>0? 1.12 : 1/1.12;
  vb.w*=f; vb.h*=f;
  vb.x = mx-(mx-vb.x)*f;
  vb.y = my-(my-vb.y)*f;
  applyVb();
},{passive:false});
function zoomBy(f){
  const mx=vb.x+vb.w/2, my=vb.y+vb.h/2;
  vb.w*=f; vb.h*=f; vb.x=mx-vb.w/2; vb.y=my-vb.h/2;
  applyVb();
}
$("zin").addEventListener("click",()=>zoomBy(1/1.25));
$("zout").addEventListener("click",()=>zoomBy(1.25));
$("zfit").addEventListener("click",()=>{ vb=Object.assign({},fitVb); applyVb(); });
function applyVb(){
  if(sceneSvg) sceneSvg.setAttribute("viewBox", vb.x+" "+vb.y+" "+vb.w+" "+vb.h);
  placeBadges();
}
window.addEventListener("resize", ()=>{ if(sceneSvg) placeBadges(); });
document.addEventListener("keydown", e=>{ if(e.key==="Escape" && chooser) closeChooser(); });

/* ---------------- dash controls ---------------- */
function dashSizes(){
  const dl = parseFloat($("dashLen").value); // 0..100
  const gl = parseFloat($("gapLen").value);
  const min = diag/300, max = diag/15;
  const dash = (min + (max-min)*(dl/100)) * unitScale.dash;
  const gap  = (min + (max-min)*(gl/100)) * unitScale.gap;
  return {dash:Math.max(dash,min/2), gap:Math.max(gap,min/2)};
}
function updateSliderLabels(){
  const {dash,gap}=dashSizes();
  $("dashVal").textContent = dash.toFixed(1)+" units";
  $("gapVal").textContent = gap.toFixed(1)+" units";
  segments.filter(s=>s.sel).forEach(paintSeg);
}
function clearPresetHighlight(){
  document.querySelectorAll(".presets button").forEach(b=> b.setAttribute("aria-pressed","false"));
}
function syncSliderFill(){
  if(window.BT && window.BT.syncRange){ window.BT.syncRange($("dashLen")); window.BT.syncRange($("gapLen")); }
}
$("dashLen").addEventListener("input", ()=>{ unitScale={dash:1,gap:1}; lastPreset="custom"; clearPresetHighlight(); updateSliderLabels(); });
$("gapLen").addEventListener("input", ()=>{ unitScale={dash:1,gap:1}; lastPreset="custom"; clearPresetHighlight(); updateSliderLabels(); });
document.querySelectorAll(".presets button").forEach(b=>{
  b.addEventListener("click", ()=>{
    $("dashLen").value = 40; $("gapLen").value = 30;
    unitScale = {dash:parseFloat(b.dataset.d), gap:parseFloat(b.dataset.g)};
    lastPreset = (b.textContent||"").trim();
    syncSliderFill();
    updateSliderLabels();
  });
});
updateSliderLabels();

/* ---------------- export ---------------- */
const fmt = n=> Math.round(n*1000)/1000;
const dist2 = (a,b)=> Math.hypot(a[0]-b[0], a[1]-b[1]);

function concatSegs(segs){
  let pts = segs[0].pts.slice();
  for(let i=1;i<segs.length;i++){
    const p = segs[i].pts;
    const drop = dist2(pts[pts.length-1], p[0]) < 1e-6 ? 1 : 0;
    pts = pts.concat(p.slice(drop));
  }
  return pts;
}
function toD(pts, close){
  return "M"+pts.map(p=>fmt(p[0])+","+fmt(p[1])).join(" L")+(close?" Z":"");
}

/* Rebuild cut geometry preserving topology:
   - source elements with nothing scored -> original markup passed through verbatim
     (fills, fill-rule, compound subpaths / cutouts intact)
   - elements with scored edges -> unscored contours and runs rebuilt as subpaths of
     ONE path element; closed contours keep Z, wraparound runs merge on closed shapes */
function buildCutMarkup(){
  const byEl = {};
  segments.forEach(s=>{
    const e = shapes[s.shape].el;
    (byEl[e] = byEl[e]||[]).push(s);
  });
  const out = {};
  Object.keys(byEl).map(Number).sort((a,b)=>a-b).forEach(e=>{
    const segsAll = byEl[e];
    if(segsAll.every(s=>!s.sel)){
      out[e] = originals[e];
      return;
    }
    const byShape = {};
    segsAll.forEach(s=>{ (byShape[s.shape] = byShape[s.shape]||[]).push(s); });
    const subDs = [];
    Object.keys(byShape).forEach(k=>{
      const segs = byShape[k].sort((a,b)=>a.idx-b.idx);
      const closed = shapes[k].closed;
      if(segs.every(s=>!s.sel)){
        let pts = concatSegs(segs);
        if(closed && dist2(pts[0], pts[pts.length-1]) < 1e-6) pts = pts.slice(0,-1);
        subDs.push(toD(pts, closed));
        return;
      }
      let runs=[], cur=[];
      segs.forEach(s=>{
        if(!s.sel) cur.push(s);
        else { if(cur.length) runs.push(cur); cur=[]; }
      });
      if(cur.length) runs.push(cur);
      if(closed && runs.length>1 && !segs[0].sel && !segs[segs.length-1].sel){
        const first = runs.shift(), last = runs.pop();
        runs.push(last.concat(first));
      }
      runs.forEach(r=> subDs.push(toD(concatSegs(r), false)));
    });
    out[e] = subDs.length ? '<path fill="none" d="'+subDs.join(" ")+'"/>' : "";
  });
  return out;
}

/* ---------------- email gate ----------------
   The popup and email step come from the shared core (BT.runWithProgress): one email on
   ANY Bearly tool unlocks every tool, signed-in customers skip it, the file still comes
   if Klaviyo fails, and the ×, Escape and a backdrop click close it WITHOUT the file
   (the design and selection stay as they are; the next download asks again). */
const signedIn = ()=> !!((window.PS_CUSTOMER && window.PS_CUSTOMER.email) || (window.BT_CUSTOMER && window.BT_CUSTOMER.email));

/* ---------------- tracking ----------------
   GA4 (all visitors, including anonymous): through the shared core, window.BT.track.
   Event names already carry the ps_ prefix, so the core sends them unchanged.
   Klaviyo (known email only, via BT.klaviyoEvent which adds tool + tool_handle):
   "Started PerfectScore" / "Used PerfectScore" (after the file is saved). */
function dl(event, props){
  try{
    if(window.BT && typeof window.BT.track === "function"){ window.BT.track(event, props); return; }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, props || {}));
  }catch(e){}
}

function trackEvent(name, props){
  try{
    if(window.BT && typeof window.BT.klaviyoEvent === "function") window.BT.klaviyoEvent(name, props);
  }catch(e){}
}

function trackUse(nScore, nDash){
  const {dash,gap} = dashSizes();
  trackEvent("Used PerfectScore", {
    file_name:     designMeta.file_name || "unknown",
    lines_scored:  nScore,
    dashes:        nDash,
    line_count:    designMeta.line_count || 0,
    stack_count:   designMeta.stack_count || 0,
    percent_scored: designMeta.line_count ? Math.round(nScore/designMeta.line_count*100) : 0,
    dash_length:   Math.round(dash*100)/100,
    gap_length:    Math.round(gap*100)/100,
    preset:        lastPreset,
    source_app:    designMeta.source_app || "Unknown",
    used_sample:   !!designMeta.used_sample,
    signed_in:     signedIn()
  });
}

/* which pieces (elements) get a copy of this line's dashes */
function dashTargets(s){
  const own = shapes[s.shape].el;
  if(s.stack===null) return [own];
  const st = stacks[s.stack];
  const set = {};
  (st.layers||[]).forEach(L=>{ if(L.seg!==null && segById(L.seg).sel) set[L.el] = true; });
  Object.keys(st.extraOn||{}).forEach(e=>{ set[+e] = true; });
  let out = Object.keys(set).map(Number);
  // a bare score line sitting on exactly one piece: its dashes belong to that piece
  if(!out.length && st.layers && st.layers.length===1) out = [st.layers[0].el];
  if(!out.length) out = st.ids.filter(id=> segById(id).sel).map(id=> shapes[segById(id).shape].el);
  return out.length ? out : [own];
}
$("exportBtn").addEventListener("click", ()=>{
  const {dash,gap}=dashSizes();
  const cutByEl = buildCutMarkup();                       // elIdx -> markup
  const dashByEl = {};                                    // elIdx -> ['<path d=…/>', …]
  let nScore = 0, nDash = 0;
  segments.filter(s=>s.sel).forEach(s=>{
    nScore++;
    if(!drawsDashes(s)) return;                           // one geometry per layer set
    const paths = dashesForSeg(s, dash, gap).map(piece=> '<path d="M'+piece.map(p=>fmt(p[0])+","+fmt(p[1])).join(" L")+'"/>');
    nDash += paths.length;
    dashTargets(s).forEach(e=>{ dashByEl[e] = (dashByEl[e]||[]).concat(paths); });
  });
  const vbOut = origRoot.vbAttr || (fitVb.x+" "+fitVb.y+" "+fitVb.w+" "+fitVb.h);
  const sw = Math.max(diag/600, 0.1).toFixed(2);
  // one group per piece: its cut lines, then its score dashes — so each piece can be
  // attached to its own dashes in the cutting software
  const els = Object.keys(cutByEl).map(Number).sort((a,b)=>a-b).filter(e=> cutByEl[e] || dashByEl[e]);
  const groups = els.map(e=>{
    let g = '  <g id="piece-'+(e+1)+'">\n';
    if(cutByEl[e]) g += '    <g class="cut-lines" fill="none" stroke="#000000" stroke-width="'+sw+'">\n      '+cutByEl[e]+'\n    </g>\n';
    if(dashByEl[e]) g += '    <g class="score-dashes" fill="none" stroke="#ee0000" stroke-width="'+sw+'">\n      '+dashByEl[e].join("\n      ")+'\n    </g>\n';
    return g + '  </g>';
  });
  // keep the uploaded file's physical size (its width/height), then write it at 72 units/in
  const q = v => String(v).replace(/["<>&]/g, "");
  const sizeAttrs = (origRoot.wAttr ? ' width="'+q(origRoot.wAttr)+'"' : '') + (origRoot.hAttr ? ' height="'+q(origRoot.hAttr)+'"' : '');
  const raw =
'<svg xmlns="http://www.w3.org/2000/svg"'+sizeAttrs+' viewBox="'+vbOut+'">\n'+groups.join("\n")+'\n<'+'/svg>';
  const svg = window.BT && BT.trueSize ? BT.trueSize(raw) : raw;

  const presetAtClick = lastPreset;

  // Save the file (the popup's work step): fires ps_download_click + "Used PerfectScore".
  const saveFile = info=>{
    const blob = new Blob([svg], {type:"image/svg+xml"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (window.BT && BT.brandName ? BT.brandName(fileName+" - BA.svg") : fileName+" - BA.svg");
    a.click();
    URL.revokeObjectURL(a.href);
    // fired only when the file is actually delivered (the upsell listens for it)
    dl("ps_download_click", {
      ps_file_name:    designMeta.file_name || "unknown",
      ps_lines_scored: nScore,
      ps_dashes:       nDash,
      ps_preset:       presetAtClick,
      ps_source_app:   designMeta.source_app || "Unknown",
      ps_gated:        !!(info && info.gated)
    });
    trackUse(nScore, nDash);
  };

  $("exportBtn").disabled = true;
  if(!(window.BT && window.BT.runWithProgress)){
    // old core cached: no popup, just save the file
    try{ saveFile({ gated: false }); } finally { updateSelUI(); }
    return;
  }
  window.BT.runWithProgress({
    title:    "Scoring your design",
    stages: [
      {p:16, wait:350,  text:"Reading your design…"},
      {p:48, wait:950,  text:"Scoring "+nScore+" line"+(nScore===1?"":"s")+"…"},
      {p:84, wait:1000, text:"Placing "+nDash+" perfect dashes…"}
    ],
    gateText: "Your file is almost ready! Enter your email to reveal your results.",
    doneText: "Your file is ready!",
    work:     saveFile,
    onEnd:    r=>{
      updateSelUI();
      if(r.status === "dismissed"){ const eb = $("exportBtn"); if(eb && !eb.disabled){ try{ eb.focus(); }catch(e){} } }
    }
  }).then(r=>{ if(r.status === "busy") updateSelUI(); });
});

/* ---------------- intersection-aware dashes ----------------
   A dash is dropped when another line CROSSES it or ENDS on it (T-junction), so
   intersections stay uncut and nothing gets severed at a crossing. Lines that only come
   close — converging edges at a pointed tip, a neighbour's outline running alongside —
   leave the dash alone.
   Which lines count: lines in the same piece (top-level group), plus any line that is
   physically connected to the scored line's shape. Overlapping pieces in a layered design
   are not connected, so their outlines no longer cull dashes. */
function bboxOf(pts){
  let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity;
  for(const p of pts){
    if(p[0]<x0)x0=p[0]; if(p[0]>x1)x1=p[0];
    if(p[1]<y0)y0=p[1]; if(p[1]>y1)y1=p[1];
  }
  return [x0,y0,x1,y1];
}
function ptSegDist(p,a,b){
  const vx=b[0]-a[0], vy=b[1]-a[1];
  const L2=vx*vx+vy*vy;
  let t = L2 ? ((p[0]-a[0])*vx+(p[1]-a[1])*vy)/L2 : 0;
  t = Math.max(0,Math.min(1,t));
  return Math.hypot(p[0]-(a[0]+t*vx), p[1]-(a[1]+t*vy));
}
function segCross(a,b,c,d){
  const o=(p,q,r)=>(q[0]-p[0])*(r[1]-p[1])-(q[1]-p[1])*(r[0]-p[0]);
  const d1=o(c,d,a), d2=o(c,d,b), d3=o(a,b,c), d4=o(a,b,d);
  return ((d1>0&&d2<0)||(d1<0&&d2>0)) && ((d3>0&&d4<0)||(d3<0&&d4>0));
}
function nearParallel(a,b,c,d){
  const ux=b[0]-a[0], uy=b[1]-a[1], vx=d[0]-c[0], vy=d[1]-c[1];
  const lu=Math.hypot(ux,uy), lv=Math.hypot(vx,vy);
  if(!lu || !lv) return true;
  return Math.abs(ux*vy-uy*vx)/(lu*lv) < 0.2;   // < ~11 degrees apart
}
/* is segment o connected to segment s? (an end of one lies on the other) */
const connCache = {};
function connected(s, o){
  const key = s.id<o.id ? s.id+":"+o.id : o.id+":"+s.id;
  if(connCache[key]!==undefined) return connCache[key];
  const tol = Math.max(diag/400, 0.5);
  const endsOn = (a,b)=> minDistToPoly(a.pts[0], b.pts) < tol || minDistToPoly(a.pts[a.pts.length-1], b.pts) < tol;
  const r = endsOn(s,o) || endsOn(o,s);
  connCache[key] = r;
  return r;
}
/* does line o count as a crossing candidate for scored line s? */
function countsAgainst(s, o){
  if(o.id===s.id) return false;
  if(s.stack!==null && o.stack===s.stack) return false;    // its own stacked copies
  if(shapes[o.shape].piece !== shapes[s.shape].piece && !connected(s,o)) return false;
  return true;
}
function bboxApart(a, b, tol){
  return a[0]-tol>b[2] || a[2]+tol<b[0] || a[1]-tol>b[3] || a[3]+tol<b[1];
}
function dashHitsOther(piece, s, tol){
  const pb = bboxOf(piece);
  const touch = tol*0.5;
  for(const o of segments){
    if(!countsAgainst(s,o)) continue;
    if(!o._bb) o._bb = bboxOf(o.pts);
    if(bboxApart(pb, o._bb, tol)) continue;
    const oLast = o.pts.length-1, pLast = piece.length-1;
    for(let i=1;i<piece.length;i++){
      for(let j=1;j<o.pts.length;j++){
        const a=piece[i-1], b=piece[i], c=o.pts[j-1], d=o.pts[j];
        // a duplicate or parallel line lying along the dash is not a crossing
        if(nearParallel(a,b,c,d)) continue;
        if(segCross(a,b,c,d)) return true;                             // real crossing
        if(j===1     && ptSegDist(c,a,b) < touch) return true;          // other line starts on the dash
        if(j===oLast && ptSegDist(d,a,b) < touch) return true;          // other line ends on the dash
        if(i===1     && ptSegDist(a,c,d) < touch) return true;          // dash begins on the other line
        if(i===pLast && ptSegDist(b,c,d) < touch) return true;          // dash ends on the other line
      }
    }
  }
  return false;
}
/* intersection point of segments ab and cd (assumes they cross) */
function crossPoint(a,b,c,d){
  const r=[b[0]-a[0], b[1]-a[1]], q=[d[0]-c[0], d[1]-c[1]];
  const den = r[0]*q[1]-r[1]*q[0];
  if(Math.abs(den)<1e-12) return null;
  const t = ((c[0]-a[0])*q[1]-(c[1]-a[1])*q[0])/den;
  return [a[0]+r[0]*t, a[1]+r[1]*t];
}
function projT(p,a,b){
  const vx=b[0]-a[0], vy=b[1]-a[1]; const L2=vx*vx+vy*vy;
  return L2 ? Math.max(0,Math.min(1,((p[0]-a[0])*vx+(p[1]-a[1])*vy)/L2)) : 0;
}
function cumLens(pts){
  const cum=[0];
  for(let i=1;i<pts.length;i++) cum.push(cum[i-1]+Math.hypot(pts[i][0]-pts[i-1][0],pts[i][1]-pts[i-1][1]));
  return cum;
}
function polyAt(pts, cum, s){
  let i=1;
  while(i<cum.length && cum[i]<s) i++;
  if(i>=cum.length) return pts[pts.length-1].slice();
  const t=(s-cum[i-1])/((cum[i]-cum[i-1])||1);
  return [pts[i-1][0]+(pts[i][0]-pts[i-1][0])*t, pts[i-1][1]+(pts[i][1]-pts[i-1][1])*t];
}
/* where do other lines cross or end on s? (arc-length positions along s) */
function junctionsOn(s, tol){
  const touch = tol*0.5;
  const cum = cumLens(s.pts), total = cum[cum.length-1];
  if(!s._bb) s._bb = bboxOf(s.pts);
  const js=[];
  for(const o of segments){
    if(!countsAgainst(s,o)) continue;
    if(!o._bb) o._bb = bboxOf(o.pts);
    if(bboxApart(s._bb, o._bb, tol)) continue;
    const oLast = o.pts.length-1;
    for(let i=1;i<s.pts.length;i++){
      const a=s.pts[i-1], b=s.pts[i], lab=cum[i]-cum[i-1];
      for(let j=1;j<o.pts.length;j++){
        const c=o.pts[j-1], d=o.pts[j];
        if(nearParallel(a,b,c,d)) continue;
        if(segCross(a,b,c,d)){ const x=crossPoint(a,b,c,d); if(x) js.push(cum[i-1]+Math.hypot(x[0]-a[0],x[1]-a[1])); continue; }
        if(j===1     && ptSegDist(c,a,b) < touch) js.push(cum[i-1]+projT(c,a,b)*lab);
        if(j===oLast && ptSegDist(d,a,b) < touch) js.push(cum[i-1]+projT(d,a,b)*lab);
      }
    }
  }
  // junctions at the very ends are already handled by the gap-first layout
  const out=[];
  js.filter(v=> v>tol && v<total-tol).sort((x,y)=>x-y).forEach(v=>{ if(!out.length || v-out[out.length-1]>tol) out.push(v); });
  return out;
}
/* split a scored line into spans at its junctions so every span is dashed gap-first:
   a T-junction or crossing always lands in a gap, and short lines still get a dash
   on each side of it instead of losing their only dash */
function spansForSeg(s, tol){
  let pts = s.pts, cum = cumLens(pts), total = cum[cum.length-1];
  let js = junctionsOn(s, tol);
  if(!js.length) return [{pts: pts, len: total}];
  const isLoop = dist2(pts[0], pts[pts.length-1]) < 1e-6;
  if(isLoop){
    // rotate the loop so it starts at the first junction, then treat it as an open line
    const j0 = js[0];
    const rot = [polyAt(pts,cum,j0)];
    for(let i=0;i<pts.length;i++) if(cum[i]>j0) rot.push(pts[i].slice());
    for(let i=1;i<pts.length;i++) if(cum[i]<j0) rot.push(pts[i].slice());
    rot.push(polyAt(pts,cum,j0));
    pts = dedupe(rot); cum = cumLens(pts); total = cum[cum.length-1];
    js = js.slice(1).map(v=> v-j0);
    if(!js.length) return [{pts: pts, len: total}];
  }
  const cuts = [0].concat(js, [total]);
  const spans=[];
  for(let k=1;k<cuts.length;k++){
    const s0=cuts[k-1], s1=cuts[k];
    if(s1-s0 < tol) continue;
    const sp=[polyAt(pts,cum,s0)];
    for(let i=0;i<pts.length;i++) if(cum[i]>s0 && cum[i]<s1) sp.push(pts[i].slice());
    sp.push(polyAt(pts,cum,s1));
    const d = dedupe(sp);
    if(d.length>1) spans.push({pts:d, len: s1-s0});
  }
  return spans;
}
function dashesForSeg(s, dash, gap){
  const tol = gap*0.5;
  const out=[];
  spansForSeg(s, tol).forEach(sp=>{
    dashesFor(sp.pts, sp.len, dash, gap).forEach(p=>{ if(!dashHitsOther(p, s, tol)) out.push(p); });
  });
  return out;
}

/* gap-first dashes: open lines start AND end with a gap so endpoints are never cut;
   short lines collapse to a single centered dash; loop seams fall in a gap */
function dashesFor(pts, total, dash, gap){
  const isLoop = dist2(pts[0], pts[pts.length-1]) < 1e-6;
  let n, D, G, offset;
  if(isLoop && total > (dash+gap)*2){
    n = Math.max(3, Math.round(total/(dash+gap)));
    const f = total/(n*(dash+gap));
    D = dash*f; G = gap*f; offset = G/2;
  }else{
    n = Math.max(1, Math.round((total-gap)/(dash+gap)));
    const f = total / (n*dash + (n+1)*gap);
    D = dash*f; G = gap*f; offset = G;
  }
  const cum = cumLens(pts);
  const at = s=> polyAt(pts, cum, s);
  const pieces=[];
  for(let k=0;k<n;k++){
    const s0=offset+k*(D+G), s1=s0+D;
    const piece=[at(s0)];
    for(let i=0;i<cum.length;i++) if(cum[i]>s0 && cum[i]<s1) piece.push(pts[i].slice());
    piece.push(at(Math.min(s1,total)));
    pieces.push(piece);
  }
  return pieces;
}

})();